import { isIdentificatorElementType } from '@/common/utils/panelGetDataCollect'

export type RegisteredElementRef = {
  fieldName?: unknown
  name?: unknown
  formId?: unknown
  elementId?: unknown
  elementType?: unknown
  type?: unknown
  getType?: () => unknown
  ctx?: { id?: unknown }
}

function widgetType(el: RegisteredElementRef): unknown {
  if (el.elementType != null && String(el.elementType).trim() !== '') return el.elementType
  if (el.type != null && String(el.type).trim() !== '') return el.type
  if (typeof el.getType === 'function') {
    try {
      return el.getType()
    } catch {
      return undefined
    }
  }
  return undefined
}

function sortFillableFirst<T extends RegisteredElementRef>(candidates: T[]): T[] {
  return [...candidates].sort((a, b) => {
    const aIdent = isIdentificatorElementType(widgetType(a)) ? 1 : 0
    const bIdent = isIdentificatorElementType(widgetType(b)) ? 1 : 0
    return aIdent - bIdent
  })
}

function widgetIdentity(el: RegisteredElementRef): string {
  const formId = String(el.formId ?? '').trim()
  const elementId = String(el.elementId ?? el.ctx?.id ?? '').trim()
  if (formId || elementId) return `${formId}:${elementId}`
  return ''
}

function matchesName(el: RegisteredElementRef, name: string): boolean {
  return String(el.fieldName ?? '').trim() === name || String(el.name ?? '').trim() === name
}

function matchesElementId(el: RegisteredElementRef, id: number): boolean {
  return Number(el.elementId) === id || Number(el.ctx?.id) === id
}

function uniqueWidgets(candidates: RegisteredElementRef[]): RegisteredElementRef[] {
  const seen = new Set<string>()
  const out: RegisteredElementRef[] = []
  for (const el of candidates) {
    const id = widgetIdentity(el)
    const key = id || `__anon_${out.length}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(el)
  }
  return out
}

function wantedFormId(formId?: string | number): string {
  if (formId == null) return ''
  const wanted = String(formId).trim()
  return wanted !== '' && wanted !== '-1' ? wanted : ''
}

function formScope<T extends RegisteredElementRef>(
  candidates: T[],
  formId?: string | number,
): T[] {
  const wanted = wantedFormId(formId)
  if (!wanted) return candidates
  const scoped = candidates.filter((el) => String(el.formId ?? '').trim() === wanted)
  return scoped.length > 0 ? scoped : candidates
}

function flattenMaps<T extends RegisteredElementRef>(
  elMap: Record<string, T>,
  extraMaps?: Array<Record<string, T> | null | undefined>,
): T[] {
  const acc: T[] = [...Object.values(elMap)]
  if (extraMaps) {
    for (const map of extraMaps) {
      if (!map) continue
      acc.push(...Object.values(map))
    }
  }
  return uniqueWidgets(acc) as T[]
}

/**
 * Swing `NameFilter` + current form: every widget with this fieldName (or numeric id).
 * Fillable (TEXT) before Identificator — same order as XML/registry.
 */
export function collectRegisteredElementsByName<T extends RegisteredElementRef>(
  elMap: Record<string, T>,
  name: string | number,
  formId?: string | number,
  extraMaps?: Array<Record<string, T> | null | undefined>,
): T[] {
  const key = typeof name === 'number' ? String(name) : String(name ?? '').trim()
  if (!key) return []

  const values = flattenMaps(elMap, extraMaps)
  const matches = /^\d+$/.test(key)
    ? (values.filter((el) => matchesElementId(el, parseInt(key, 10))) as T[])
    : (values.filter((el) => matchesName(el, key)) as T[])
  return sortFillableFirst(formScope(matches, formId))
}

/**
 * Element lookup by name or numeric id, preferring widgets of the current form
 * when the same fieldName is registered on parent and dialog.
 * Swing `getRegistryItemByName`: first fillable on the form, not last-registered Identificator.
 */
export function resolveRegisteredElementByName<T extends RegisteredElementRef>(
  elMap: Record<string, T>,
  name: string | number,
  formId?: string | number,
  extraMaps?: Array<Record<string, T> | null | undefined>,
): T | undefined {
  const key = typeof name === 'number' ? String(name) : String(name ?? '').trim()
  if (!key) return undefined

  const collected = collectRegisteredElementsByName(elMap, name, formId, extraMaps)
  if (collected.length > 0) return collected[0]
  return elMap[key]
}
